import { useCallback, useEffect, useRef } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

// SAMPLE OUTPUT OF LOG PERCENTAGE GENERATOR:
// Standard: 0% --- Log: 29%
// Standard: 20% --- Log: 29%
// Standard: 40% --- Log: 35%
// Standard: 60% --- Log: 64%
// Standard: 80% --- Log: 84%
// Standard: 100% --- Log: 100%

const TIMEOUT_PERIOD_MS = 500;
const MAX_PERCENTAGE = 100;

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

// given a value from 0 to MATH.E, computes the log scale percentage
const calculateNaturalLogScalePercentage = (value) => {
  const shift = 0.4; // SHIFT to avoid negative or negligible percentage values
  const logVal = clamp(Math.log(value), 0, 1);
  const shiftedValue = logVal + shift;
  const percentage = (shiftedValue / (1 + shift)) * MAX_PERCENTAGE;
  return Math.round(percentage);
};

// converts a linearPercentage to a natural log variant
const convertToNaturalLogScale = (percentage) => {
  return (percentage / MAX_PERCENTAGE) * Math.E;
};

const calculateNaturalLogPercentage = (percentage) => {
  const naturalLogScaleValue = convertToNaturalLogScale(percentage);
  return calculateNaturalLogScalePercentage(naturalLogScaleValue);
};

export const useProgress = (estimatedMilliseconds) => {
  const [percentageComplete, setPercentageComplete] = useAsyncSafeState(0);
  const [shouldProgress, setShouldProgress] = useAsyncSafeState(false);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (!shouldProgress) {
      return;
    }
    startTime.current = Date.now();

    const intervalId = window.setInterval(() => {
      const timePercent = ((Date.now() - startTime.current) / estimatedMilliseconds) * 100;
      setPercentageComplete(Math.min(calculateNaturalLogPercentage(timePercent), MAX_PERCENTAGE));
    }, TIMEOUT_PERIOD_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [shouldProgress, setPercentageComplete, estimatedMilliseconds]);

  const startProgress = useCallback(() => {
    setShouldProgress(true);
  }, [setShouldProgress]);

  const resetProgress = useCallback(() => {
    setPercentageComplete(0);
    setShouldProgress(false);
  }, [setShouldProgress, setPercentageComplete]);

  return {
    percentageComplete,
    startProgress,
    resetProgress,
    progressing: shouldProgress,
  };
};
